import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('niki_access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('niki_refresh_token')
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          })

          const { access_token, refresh_token: newRefreshToken } = response.data
          localStorage.setItem('niki_access_token', access_token)
          localStorage.setItem('niki_refresh_token', newRefreshToken)

          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        }
      } catch {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('niki_access_token')
        localStorage.removeItem('niki_refresh_token')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    // OAuth2 expects form data with 'username' field (can be email or username)
    const formData = new URLSearchParams()
    formData.append('username', email)
    formData.append('password', password)

    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
    return response.data
  },

  logout: async () => {
    const response = await api.post('/auth/logout')
    localStorage.removeItem('niki_access_token')
    localStorage.removeItem('niki_refresh_token')
    return response.data
  },

  me: async () => {
    const response = await api.get('/auth/me')
    return response.data
  },
}

// Chat API
export const chatApi = {
  sendMessage: async (message: string, conversationId?: string) => {
    const response = await api.post('/chat', {
      message,
      conversation_id: conversationId,
    })
    return response.data
  },

  getConversations: async () => {
    const response = await api.get('/chat/conversations')
    return response.data
  },

  getConversation: async (id: string) => {
    const response = await api.get(`/chat/conversations/${id}`)
    return response.data
  },
}

// Tasks API
export const tasksApi = {
  getTasks: async () => {
    const response = await api.get('/tasks')
    return response.data
  },

  getTask: async (id: string) => {
    const response = await api.get(`/tasks/${id}`)
    return response.data
  },

  cancelTask: async (id: string) => {
    const response = await api.post(`/tasks/${id}/cancel`)
    return response.data
  },
}

// Kill Switch API
export const killSwitchApi = {
  activate: async (reason?: string) => {
    const response = await api.post('/kill-switch', { reason })
    return response.data
  },
}

// Settings API
export const settingsApi = {
  getSettings: async () => {
    const response = await api.get('/settings')
    return response.data
  },

  updateSettings: async (settings: Record<string, unknown>) => {
    const response = await api.patch('/settings', settings)
    return response.data
  },
}

export default api
